import React, { useState, useEffect } from 'react';
import {
  LuActivity,
  LuCpu,
  LuHardDrive,
  LuWifi,
  LuUsers,
  LuClock,
  LuTrendingUp,
  LuTrendingDown,
  LuRefreshCw,
  LuDownload,
  LuAlertTriangle,
  LuCheckCircle,
  LuBarChart3,
  LuPieChart,
  LuLineChart,
  LuX,
} from 'react-icons/lu';

const Usage = ({ node, config, onConfigChange, loading, error, onSave, onDelete, saved }) => {
  const [timeRange, setTimeRange] = useState('24h');
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Sample metrics data
  const [metrics] = useState({
    realtime: {
      cpu: 45.2,
      memory: 68.5,
      disk: 23.1,
      network: 12.8,
      connections: 147,
      uptime: 2847, // minutes
      requests: 1250,
      errors: 3
    },
    alerts: [
      {
        id: 1,
        type: 'warning',
        message: 'High memory usage detected',
        timestamp: new Date('2024-06-14T10:30:00'),
        value: '68.5%',
        threshold: '65%'
      },
      {
        id: 2,
        type: 'info',
        message: 'Backup completed successfully',
        timestamp: new Date('2024-06-14T09:15:00'),
        value: '2.3GB',
        threshold: null
      }
    ],
    historical: {
      cpu: [
        { time: '00:00', value: 32 },
        { time: '04:00', value: 28 },
        { time: '08:00', value: 55 },
        { time: '12:00', value: 67 },
        { time: '16:00', value: 45 },
        { time: '20:00', value: 42 }
      ],
      memory: [
        { time: '00:00', value: 45 },
        { time: '04:00', value: 42 },
        { time: '08:00', value: 58 },
        { time: '12:00', value: 72 },
        { time: '16:00', value: 68 },
        { time: '20:00', value: 65 }
      ],
      requests: [
        { time: '00:00', value: 450 },
        { time: '04:00', value: 320 },
        { time: '08:00', value: 890 },
        { time: '12:00', value: 1250 },
        { time: '16:00', value: 980 },
        { time: '20:00', value: 750 }
      ]
    }
  });

  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
        // Simulate real-time updates
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isLive]);

  const formatUptime = (minutes) => {
    const days = Math.floor(minutes / (24 * 60));
    const hours = Math.floor((minutes % (24 * 60)) / 60);
    const mins = minutes % 60;
    return `${days}d ${hours}h ${mins}m`;
  };

  const getStatusColor = (value, thresholds) => {
    if (value >= thresholds.critical) return 'text-red-400';
    if (value >= thresholds.warning) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning':
        return <LuAlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'error':
        return <LuAlertTriangle className="w-4 h-4 text-red-400" />;
      case 'info':
        return <LuCheckCircle className="w-4 h-4 text-blue-400" />;
      default:
        return <LuCheckCircle className="w-4 h-4 text-green-400" />;
    }
  };

  const MetricCard = ({ title, value, unit, icon, change, threshold, status = 'normal' }) => (
    <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-white/80 text-sm">{title}</span>
        </div>
        {change && (
          <div className={`flex items-center gap-1 text-xs ${change > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {change > 0 ? <LuTrendingUp className="w-3 h-3" /> : <LuTrendingDown className="w-3 h-3" />}
            {Math.abs(change)}%
          </div>
        )}
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className={`text-2xl font-medium ${
            status === 'warning' ? 'text-yellow-400' :
            status === 'critical' ? 'text-red-400' : 'text-white'
          }`}>
            {value}
            <span className="text-white/60 text-base ml-1">{unit}</span>
          </div>
          {threshold && (
            <div className="text-white/40 text-xs mt-1">
              Threshold: {threshold}
            </div>
          )}
        </div>

        {/* Simple progress bar */}
        {unit === '%' && (
          <div className="w-16 h-2 bg-[#ffffff10] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                status === 'warning' ? 'bg-yellow-400' :
                status === 'critical' ? 'bg-red-400' : 'bg-green-400'
              }`}
              style={{ width: `${Math.min(value, 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );

  const SimpleChart = ({ data, color = '#3b82f6' }) => (
    <div className="h-20 flex items-end gap-1">
      {data.map((point, index) => (
        <div
          key={index}
          className="flex-1 bg-blue-500/20 rounded-t"
          style={{
            height: `${(point.value / Math.max(...data.map(d => d.value))) * 100}%`,
            minHeight: '2px'
          }}
          title={`${point.time}: ${point.value}`}
        />
      ))}
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-medium text-lg flex items-center gap-2">
              <LuActivity className="w-5 h-5" />
              Server Usage & Monitoring
            </h3>
            <p className="text-white/60 text-sm mt-1">
              Real-time performance metrics and resource utilization
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsLive(!isLive)}
              className={`px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                isLive 
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                  : 'bg-[#ffffff10] text-white/60 hover:bg-[#ffffff15]'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-400 animate-pulse' : 'bg-white/40'}`} />
              {isLive ? 'Live' : 'Paused'}
            </button>
            <button className="p-2 bg-[#ffffff10] hover:bg-[#ffffff15] text-white/60 rounded transition-colors" title="Download report">
              <LuDownload className="w-4 h-4" />
            </button>
            <button className="p-2 bg-[#ffffff10] hover:bg-[#ffffff15] text-white/60 rounded transition-colors" title="Refresh data">
              <LuRefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          <span className="text-white/60 text-sm">Time Range:</span>
          <div className="flex items-center gap-1 bg-[#ffffff05] p-1 rounded">
            {['1h', '6h', '24h', '7d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  timeRange === range
                    ? 'bg-blue-500 text-white'
                    : 'text-white/60 hover:text-white/80 hover:bg-[#ffffff05]'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
          <span className="text-white/40 text-xs ml-auto">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Real-time Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="CPU Usage"
          value={metrics.realtime.cpu}
          unit="%"
          icon={<LuCpu className="w-4 h-4 text-blue-400" />}
          change={2.3}
          threshold="80%"
          status={metrics.realtime.cpu > 65 ? 'warning' : 'normal'}
        />
        <MetricCard
          title="Memory"
          value={metrics.realtime.memory}
          unit="%"
          icon={<LuHardDrive className="w-4 h-4 text-green-400" />}
          change={-1.2}
          threshold="85%"
          status={metrics.realtime.memory > 65 ? 'warning' : 'normal'}
        />
        <MetricCard
          title="Disk I/O"
          value={metrics.realtime.disk}
          unit="MB/s"
          icon={<LuHardDrive className="w-4 h-4 text-purple-400" />}
          change={5.7}
        />
        <MetricCard
          title="Network"
          value={metrics.realtime.network}
          unit="MB/s"
          icon={<LuWifi className="w-4 h-4 text-orange-400" />}
          change={-3.1}
        />
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <LuUsers className="w-4 h-4 text-blue-400" />
              <span className="text-white/80 text-sm">Active Connections</span>
            </div>
          </div>
          <div className="text-2xl font-medium text-white">{metrics.realtime.connections}</div>
          <div className="text-white/60 text-xs mt-1">Peak: 203 (12:34 PM)</div>
        </div>

        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <LuClock className="w-4 h-4 text-green-400" />
              <span className="text-white/80 text-sm">Uptime</span>
            </div>
          </div>
          <div className="text-2xl font-medium text-white">{formatUptime(metrics.realtime.uptime)}</div>
          <div className="text-white/60 text-xs mt-1">Since: June 12, 2024</div>
        </div>

        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <LuActivity className="w-4 h-4 text-purple-400" />
              <span className="text-white/80 text-sm">Requests/Hour</span>
            </div>
          </div>
          <div className="text-2xl font-medium text-white">{metrics.realtime.requests.toLocaleString()}</div>
          <div className="text-white/60 text-xs mt-1">
            <span className="text-red-400">{metrics.realtime.errors}</span> errors
          </div>
        </div>
      </div>

      {/* Historical Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium flex items-center gap-2">
              <LuLineChart className="w-4 h-4" />
              CPU Usage
            </h4>
            <span className="text-white/60 text-xs">{timeRange}</span>
          </div>
          <SimpleChart data={metrics.historical.cpu} color="#3b82f6" />
          <div className="flex justify-between text-xs text-white/60 mt-2">
            <span>Min: 28%</span>
            <span>Avg: 45%</span>
            <span>Max: 67%</span>
          </div>
        </div>

        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium flex items-center gap-2">
              <LuBarChart3 className="w-4 h-4" />
              Memory Usage
            </h4>
            <span className="text-white/60 text-xs">{timeRange}</span>
          </div>
          <SimpleChart data={metrics.historical.memory} color="#10b981" />
          <div className="flex justify-between text-xs text-white/60 mt-2">
            <span>Min: 42%</span>
            <span>Avg: 58%</span>
            <span>Max: 72%</span>
          </div>
        </div>

        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-medium flex items-center gap-2">
              <LuPieChart className="w-4 h-4" />
              Request Rate
            </h4>
            <span className="text-white/60 text-xs">{timeRange}</span>
          </div>
          <SimpleChart data={metrics.historical.requests} color="#8b5cf6" />
          <div className="flex justify-between text-xs text-white/60 mt-2">
            <span>Min: 320</span>
            <span>Avg: 773</span>
            <span>Max: 1.25k</span>
          </div>
        </div>
      </div>

      {/* Alerts & Notifications */}
      <div className="mb-6">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <LuAlertTriangle className="w-4 h-4" />
          Recent Alerts ({metrics.alerts.length})
        </h4>
        <div className="space-y-2">
          {metrics.alerts.map((alert) => (
            <div
              key={alert.id}
              className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                {getAlertIcon(alert.type)}
                <div>
                  <div className="text-white/90">{alert.message}</div>
                  <div className="text-white/60 text-sm">
                    {alert.timestamp.toLocaleString()}
                    {alert.value && (
                      <span className="ml-2">
                        Current: <span className="text-white/80">{alert.value}</span>
                        {alert.threshold && (
                          <span className="ml-1">
                            (Threshold: {alert.threshold})
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button className="text-white/40 hover:text-white/60 transition-colors">
                <LuX className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Configuration Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <h4 className="text-white font-medium mb-3">Server Configuration</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-white/60">Host:</span>
              <span className="text-white/80">{config.host || 'localhost'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">Port:</span>
              <span className="text-white/80">{config.port || 3000}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">Environment:</span>
              <span className={`px-2 py-1 rounded text-xs ${
                config.environment === 'production' ? 'bg-red-500/20 text-red-400' :
                config.environment === 'staging' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {config.environment || 'development'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">Max Connections:</span>
              <span className="text-white/80">{config.maxConnections || 100}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">HTTPS:</span>
              <span className={config.enableHttps ? 'text-green-400' : 'text-red-400'}>
                {config.enableHttps ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <h4 className="text-white font-medium mb-3">Performance Summary</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Overall Health</span>
              <div className="flex items-center gap-2">
                <LuCheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">Healthy</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Resource Usage</span>
              <div className="flex items-center gap-2">
                <LuAlertTriangle className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 text-sm">Moderate</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Response Time</span>
              <span className="text-white/80 text-sm">45ms avg</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Error Rate</span>
              <span className="text-green-400 text-sm">0.24%</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Availability</span>
              <span className="text-green-400 text-sm">99.97%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Usage;